import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useEnterprise } from "@/contexts/EnterpriseContext";
import { EnterpriseNotFoundPage } from "@/pages/enterprise/enterprise-not-found-page";
import { EnterpriseNoAccessPage } from "@/pages/enterprise/enterprise-no-access-page";
import { LoadingLogo } from "@/components/shared/LoadingLogo";

interface EnterpriseRequireMembershipProps {
  children: ReactNode;
  /** When true, also requires the enterprise_admin role. */
  requireAdmin?: boolean;
}

/**
 * Route guard for enterprise-scoped pages.
 *
 * - While the enterprise or membership is loading → shows a centered spinner.
 * - Enterprise not found → renders EnterpriseNotFoundPage.
 * - Unauthenticated → redirects to /login?returnTo=<current path>.
 * - Authenticated but not a member → renders EnterpriseNoAccessPage.
 * - requireAdmin=true and user is only a reviewer → renders EnterpriseNoAccessPage.
 * - Otherwise → renders children.
 */
export function EnterpriseRequireMembership({
  children,
  requireAdmin = false,
}: EnterpriseRequireMembershipProps): JSX.Element {
  const {
    loading,
    notFound,
    enterprise,
    isAuthenticated,
    myMembership,
    membershipLoading,
    myRole,
  } = useEnterprise();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  if (notFound || !enterprise) {
    return <EnterpriseNotFoundPage />;
  }

  if (!isAuthenticated) {
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  if (membershipLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  if (!myMembership) {
    return <EnterpriseNoAccessPage />;
  }

  if (requireAdmin && myRole !== "enterprise_admin") {
    return <EnterpriseNoAccessPage />;
  }

  return <>{children}</>;
}
